import React, { useMemo } from 'react'
import { ToolkitPageLayout } from '../../../components'
import { Link, useLocation } from 'react-router-dom'

export const SocialDeterminantsHealthTrainingPage: React.FC = () => {
  const location = useLocation()

  // Determine breadcrumbs based on the referring page
  const breadcrumbs = useMemo(() => {
    const baseCrumbs = [{ label: 'Toolkit', path: '/toolkit' }]

    // Check if coming from providers, clinics, or elsewhere
    if (location.state?.from === 'providers') {
      baseCrumbs.push(
        { label: 'Providers', path: '/toolkit/providers' },
        { label: 'Training Modules', path: '/toolkit/providers/training' }
      )
    } else if (location.state?.from === 'clinics') {
      baseCrumbs.push(
        { label: 'Clinics', path: '/toolkit/clinics' },
        { label: 'Training Modules', path: '/toolkit/clinics/training' }
      )
    }

    baseCrumbs.push({
      label: 'Social Determinants of Health Training',
      path: '/toolkit/shared/social-determinants-health-training'
    })

    return baseCrumbs
  }, [location.state])

  return (
    <ToolkitPageLayout
      title="Social Determinants of Health Training"
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-8">
        {/* Goal Section */}
        <div className="bg-gray-50 p-6 border-l-4 border-[#A6410A]">
          <h4 className="text-lg font-bold text-gray-800">
            The goal for you is to understand the high burden of social
            determinants of health in people living with sickle cell disease,
            how to collect this data and utilize it to improve overall care.
          </h4>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Step 1: Review Checklist */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <ol className="list-decimal list-inside space-y-4 text-gray-700">
              <li className="text-lg">
                Review Social Determinants of Health Checklist below.
              </li>
            </ol>
            <div className="mt-4">
              <a
                href="https://docs.google.com/document/d/e/2PACX-1vRAx-60Rgdj-zLkZ2sY3JD2HfwEe6_KOsrLMs8DBkpe9slc5--YpVTjOanl_uVlWA/pub"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#A6410A] text-white rounded hover:bg-[#894d35] transition-colors"
              >
                Social Determinants of Health checklist
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Step 2: Read First Article */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <p className="text-lg text-gray-700 mb-4">
            2. Read the article linked below and review screening tools (Tables
            1-3) to decide what can be incorporated into your practice.
          </p>
          <div className="border-l-4 border-[#AF8A48] pl-4">
            <a
              href="https://ashpublications.org/hematology/article/2020/1/175/474277/Social-aspects-of-chronic-transfusions-addressing"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
            >
              <p className="text-[#A6410A] font-medium italic">
                &ldquo;Social aspects of chronic transfusions: addressing social
                determinants of health, health literacy, and quality of
                life&rdquo;, <span className="italic">Jennifer Webb (CNH)</span>
              </p>
            </a>
          </div>
        </div>

        {/* Step 3: Read Second Article */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <p className="text-lg text-gray-700 mb-4">
            3. Read the article linked below:
          </p>
          <div className="border-l-4 border-[#AF8A48] pl-4">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/31571379/"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
            >
              <p className="text-[#A6410A] font-medium">
                <span className="italic">
                  Universal screening for social determinants of health in
                  pediatric sickle cell disease: A quality-improvement
                  intiative,
                </span>{' '}
                Alexander Power-Hayes (BMC).
              </p>
            </a>
          </div>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Completion Link */}
        <div className="text-center py-8">
          <Link
            to={
              location.state?.from === 'providers'
                ? '/toolkit/providers/training'
                : location.state?.from === 'clinics'
                  ? '/toolkit/clinics/training'
                  : '/toolkit'
            }
            className="inline-block px-8 py-4 bg-[#A6410A] text-white font-semibold rounded hover:bg-[#894d35] transition-colors shadow-md hover:shadow-lg"
          >
            {location.state?.from === 'providers'
              ? 'ALL DONE? CLICK HERE TO RETURN TO THE PROVIDERS SCREEN'
              : location.state?.from === 'clinics'
                ? 'ALL DONE? CLICK HERE TO RETURN TO THE CLINICS SCREEN'
                : 'ALL DONE? CLICK HERE TO RETURN TO THE MAIN SCREEN'}
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
